import * as uuid from 'uuid';

import { DisplayElementModel } from '../../../Common/PageBuilder/models/display-element.model';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { DisplayElementWithSearchCriteriaModel } from './display-element-with-search-criteria.model';
import { MentionCustomizationModel } from '../../Shared/models/mention-customization.model';
import { BriefingSortByEnum } from '../enums/briefing-sort-by.enum';
import { EngagementEventTypeEnum } from '../../../api/mentions/enum/engagement-event-type.enum';
import { EngagementAgeEnum } from '../../../api/mentions/enum/engagement-age.enum';

export class CuratedMentionListDisplayElementModel extends DisplayElementModel implements DisplayElementWithSearchCriteriaModel {
    declare styles: MentionCustomizationModel;

    isBoundToGlobalStyles = true;
    title: string;
    mentionIds: string[];
    searchCriteriaId: number;
    searchCriteria: MentionSearchModel;
    readonly dateRangeAllowed: boolean = false;
    includeInsightsLink: boolean = false;
    blockReferenceId: string;
    mentionPreviewCount: number;
    // since the twig template will also be rendered in the backend, the curated blocks must use the api model to render
    mentions: MentionModel[];
    sortBy: BriefingSortByEnum = BriefingSortByEnum.PUBLISHED_DATE_NEWEST;

    constructor(
        heading?: string,
        mentionIds?: string[],
        mentions?: MentionModel[],
        searchCriteriaId?: number,
        searchCriteria?: MentionSearchModel,
        styles?: any,
        blockReferenceId?: string,
        mentionPreviewCount?: number,
    ) {
        super();

        this.title = heading;
        this.mentionIds = mentionIds || [];
        this.mentions = mentions || [];
        this.searchCriteriaId = searchCriteriaId;
        this.searchCriteria = searchCriteria;
        this.styles = styles && styles.clone ? styles : new MentionCustomizationModel(styles);
        if (this.styles.sortBy) {
            this.sortBy = this.styles.sortBy;
        }

        if (blockReferenceId) {
            this.blockReferenceId = blockReferenceId;
        } else {
            this.blockReferenceId = uuid.v4();
        }
        this.mentionPreviewCount = mentionPreviewCount;
    }

    canRender(): boolean {
        return this.mentions.length > 0;
    }

    clone(): CuratedMentionListDisplayElementModel {
        const newModel = new CuratedMentionListDisplayElementModel(
            this.title,
            this.mentionIds,
            this.mentions,
            this.searchCriteriaId,
            this.searchCriteria,
            this.styles.clone(),
            uuid.v4(),
            this.mentionPreviewCount,
        );
        newModel.isBoundToGlobalStyles = this.isBoundToGlobalStyles;
        newModel.sortBy = this.sortBy;
        newModel.includeInsightsLink = this.includeInsightsLink;
        newModel.displayType = this.displayType;

        return newModel;
    }

    onUpdate(): boolean {
        return true;
    }

    isEqual(other: CuratedMentionListDisplayElementModel): boolean {
        if (!other) {
            return false;
        }

        if (this.title !== other.title) {
            return false;
        }

        // don't test mention objects, just the list of mention IDs
        if (JSON.stringify(this.mentionIds) !== JSON.stringify(other.mentionIds)) {
            return false;
        }

        if (!this.searchCriteria !== !other.searchCriteria) {
            return false;
        }

        if (this.searchCriteria && !this.searchCriteria.equals(other.searchCriteria)) {
            return false;
        }

        // If the block is using a saved style, just compare that it's still using the same saved style, if it's not using a saved style, compare the values
        if (
            this.styles.styleState &&
            Number.isInteger(this.styles.styleState)
        ) {
            if (this.styles.styleState !== other.styles?.styleState) {
                return false;
            }
        } else if (!this.styles.equals(other.styles, true)) {
            return false;
        }

        if (this.isBoundToGlobalStyles !== other.isBoundToGlobalStyles) {
            return false;
        }

        if (this.sortBy !== other.sortBy) {
            return false;
        }
        if (this.includeInsightsLink !== other.includeInsightsLink) {
            return false;
        }
        if (this.blockReferenceId !== other.blockReferenceId) {
            return false;
        }
        if (this.mentionPreviewCount !== other.mentionPreviewCount) {
            return false;
        }

        return true;
    }

    setSearchCriteria(searchCriteria: MentionSearchModel) {
        this.searchCriteria = searchCriteria;
    }

    getSearchCriteria(): MentionSearchModel {
        return this.searchCriteria;
    }

    // Do nothing
    setInitialSourceSearchCriteria(searchCriteria: MentionSearchModel) {}

    sortMentions(): void {
        switch (this.sortBy) {
            case BriefingSortByEnum.TAGS: // TODO: we don't have tags yet
            case BriefingSortByEnum.MANUAL: {

                break;
            }
            case BriefingSortByEnum.PUBLISHED_DATE_NEWEST: {
                this.mentions.sort((a, b) => {
                    // A bunch of weird casting going on here mostly to make typescript hapy
                    return (+new Date(b.publishedDate as string)) - (+new Date(a.publishedDate as string));
                });

                break;
            }
            case BriefingSortByEnum.PUBLISHED_DATE_OLDEST: {
                this.mentions.sort((a, b) => {
                    // A bunch of weird casting going on here mostly to make typescript hapy
                    return (+new Date(a.publishedDate as string)) - (+new Date(b.publishedDate as string));
                });

                break;
            }
            case BriefingSortByEnum.SENTIMENT: {
                this.mentions.sort((a, b) => {
                    if (isNaN(a.sentiment) || a === null) {
                        return 1;
                    }
                    if (isNaN(b.sentiment) || b === null) {
                        return -1;
                    }
                    return b.sentiment - a.sentiment;
                });

                break;
            }
            case BriefingSortByEnum.TITLE: {
                this.mentions.sort((a, b) => {
                    const titleA = a.title && a.title.toLocaleUpperCase(); // ignore upper and lowercase
                    const titleB = b.title && b.title.toLocaleUpperCase(); // ignore upper and lowercase
                    if (!titleA && !titleB) {
                        return 0;
                    }
                    if (!titleA) {
                        return 1;
                    }
                    if (!titleB) {
                        return -1;
                    }
                    return titleA.localeCompare(titleB);
                });

                break;
            }
            case BriefingSortByEnum.AUTHOR: {
                this.mentions.sort((a, b) => {
                    const authorA = a.contactName && a.contactName.toLocaleUpperCase(); // ignore upper and lowercase
                    const authorB = b.contactName && b.contactName.toLocaleUpperCase(); // ignore upper and lowercase
                    if (!authorA && !authorB) {
                        return 0;
                    }
                    if (!authorA) {
                        return 1;
                    }
                    if (!authorB) {
                        return -1;
                    }
                    return authorA.localeCompare(authorB);
                });

                break;
            }
            case BriefingSortByEnum.MEDIA_TYPE: {
                this.mentions.sort((a, b) => {
                    const mediaTypeA = a.mediaType.toLocaleUpperCase(); // ignore upper and lowercase
                    const mediaTypeB = b.mediaType.toLocaleUpperCase(); // ignore upper and lowercase

                    return mediaTypeA.localeCompare(mediaTypeB);
                });

                break;
            }
            case BriefingSortByEnum.POTENTIAL_AUDIENCE_MOST: {
                this.mentions.sort((a, b) => {
                    if (isNaN(a.audienceReach) || a === null) {
                        return 1;
                    }
                    if (isNaN(b.audienceReach) || b === null) {
                        return -1;
                    }
                    return b.audienceReach - a.audienceReach;
                });

                break;
            }
            case BriefingSortByEnum.POTENTIAL_AUDIENCE_LEAST: {
                this.mentions.sort((a, b) => {
                    if (isNaN(a.audienceReach) || a === null) {
                        return -1;
                    }
                    if (isNaN(b.audienceReach) || b === null) {
                        return 1;
                    }
                    return a.audienceReach - b.audienceReach;
                });

                break;
            }
            case BriefingSortByEnum.DOMAIN_AUTHORITY: {
                this.mentions.sort((a, b) => {
                    if (isNaN(a.domainAuthority) || a === null) {
                        return 1;
                    }
                    if (isNaN(b.domainAuthority) || b === null) {
                        return -1;
                    }

                    if (a.domainAuthority === b.domainAuthority) {
                        // tie-breaker is impressions
                        if (isNaN(a.audienceReach) || a === null) {
                            return 1;
                        }
                        if (isNaN(b.audienceReach) || b === null) {
                            return -1;
                        }
                        return b.audienceReach - a.audienceReach;
                    }

                    return b.domainAuthority - a.domainAuthority;
                });

                break;
            }
            case BriefingSortByEnum.ENGAGEMENT_MOST: {
                // sort mentions by engagement total (excluding views)
                this.mentions.sort((a, b) => {
                    if (!a.socialData?.engagementData) {
                        return 1;
                    }

                    if (!b.socialData?.engagementData) {
                        return -1;
                    }

                    const engagementTotalA = a.socialData.engagementData
                        .filter(x => x.type !== EngagementEventTypeEnum.view)
                        .flatMap(x => x.engagementValues)
                        .filter(x => x.age === EngagementAgeEnum.latest && x.count != null)
                        .reduce((sum, current) => sum + current.count, 0) || 0;

                    const engagementTotalB = b.socialData.engagementData
                        .filter(x => x.type !== EngagementEventTypeEnum.view)
                        .flatMap(x => x.engagementValues)
                        .filter(x => x.age === EngagementAgeEnum.latest && x.count != null)
                        .reduce((sum, current) => sum + current.count, 0) || 0;

                    return engagementTotalB - engagementTotalA;
                });

                break;
            }
        }

        this.mentionIds = this.mentions.map(mention => {
            if (mention.id) {
                return mention.id;
            } else {
                return mention.mercuryId;
            }
        });
    }
}
